import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

/* =====================================================================
   EXPANDING OUR PRESENCE — Projects page variant.
   Visual treatment is intentionally identical to the About page
   `IndiaPresenceSection` for site-wide consistency. The map loads the
   shared `/public/india-states.json` (88 KB) which contains a single
   SVG path per Indian state/UT. The 9 LIFTILE-presence states are
   filled in brand orange with a Gaussian-blur halo + soft pulsing
   glow; every other state appears in subtle neutral grey.
   Only the heading subtitle and closing statement are different.
   ===================================================================== */

// Shared with About page — keep in sync if presence states change.
const PRESENCE_IDS = new Set([
  "GJ", // Gujarat
  "MH", // Maharashtra
  "RJ", // Rajasthan
  "KL", // Kerala
  "CT", // Chhattisgarh
  "MP", // Madhya Pradesh
  "UP", // Uttar Pradesh
  "DL", // NCT of Delhi
  "TR", // Tripura
]);

export default function ExpandingPresence() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch("/india-states.json")
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const states = data?.states ?? [];
  const viewBox = data?.viewBox ?? "0 0 1000 1000";

  return (
    <section
      data-testid="projects-expanding-presence"
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      {/* Top thin orange accent + soft ambient halo */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30" />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full pointer-events-none opacity-[0.14]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="heading-eyebrow mb-4">Our Network</div>
          <h2
            data-testid="presence-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            Expanding Our <span className="text-liftile-orange">Presence</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed mx-auto max-w-2xl">
            LIFTILE continues to expand its presence across India by supporting
            builders, contractors, developers, industrial projects and business
            partners with dependable construction machinery and dedicated
            customer support.
          </p>
        </motion.div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="relative mt-14 md:mt-20 max-w-3xl mx-auto"
        >
          <div className="relative aspect-[1/1] w-full rounded-3xl bg-gradient-to-br from-[#0e0e0e] via-[#0a0a0a] to-[#050505] border border-white/[0.06] overflow-hidden">
            {/* Faint blueprint grid */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
              }}
            />

            <svg
              viewBox={viewBox}
              preserveAspectRatio="xMidYMid meet"
              className="absolute inset-0 w-full h-full"
              role="img"
              aria-label="LIFTILE presence across Indian states"
            >
              <defs>
                <filter id="projPresenceGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {states.map((s, i) => {
                const isPresence = PRESENCE_IDS.has(s.id);
                return (
                  <motion.path
                    key={s.id}
                    d={s.path}
                    data-testid={`presence-state-${s.id}`}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.55,
                      delay: 0.05 + i * 0.012,
                      ease: "easeOut",
                    }}
                    fill={isPresence ? "#FF6B1A" : "#1F1F22"}
                    fillOpacity={isPresence ? 0.92 : 0.85}
                    stroke={isPresence ? "#FF8642" : "#2A2A2F"}
                    strokeWidth={isPresence ? 1.6 : 0.9}
                    strokeOpacity={isPresence ? 0.9 : 0.7}
                    filter={isPresence ? "url(#projPresenceGlow)" : undefined}
                  >
                    {isPresence && (
                      <animate
                        attributeName="fill-opacity"
                        values="0.78;1;0.78"
                        dur="3.6s"
                        repeatCount="indefinite"
                      />
                    )}
                  </motion.path>
                );
              })}
            </svg>

            {/* Legend overlay */}
            <div
              data-testid="presence-legend"
              className="absolute bottom-5 left-5 flex items-center gap-2.5 bg-black/60 backdrop-blur-md px-4 py-2.5 rounded-lg border border-white/[0.08]"
            >
              <span className="w-3 h-3 rounded-sm bg-liftile-orange shadow-[0_0_8px_rgba(255,107,26,0.85)]" />
              <span className="font-heading text-[10px] uppercase tracking-[0.22em] text-liftile-cream/85">
                States with LIFTILE Presence
              </span>
            </div>
          </div>
        </motion.div>

        {/* Closing statement — Projects-specific copy */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
          data-testid="presence-closing-statement"
          className="mt-16 md:mt-20 max-w-3xl mx-auto text-center"
        >
          <div className="mx-auto w-14 h-px bg-liftile-orange mb-7" />
          <p className="font-body italic text-liftile-cream/80 text-base md:text-lg leading-relaxed">
            Our network continues to grow across India through{" "}
            <span className="text-liftile-orange not-italic font-semibold">
              quality engineering, trusted partnerships and dependable
              construction machinery.
            </span>{" "}
            Every new region strengthens our commitment to serving the
            construction industry with reliability, safety and long-term
            support.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
